import React, { useState, useEffect } from 'react';
import { Star, Send, ThumbsUp, ThumbsDown, TrendingUp, CheckCircle, Sparkles, User } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface ReputationContentProps {
  animationDelay?: number;
}

const ReputationContent: React.FC<ReputationContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [gaugeValue, setGaugeValue] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [showSecondNotification, setShowSecondNotification] = useState(false);
  const [buttonPulse, setButtonPulse] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [platformRatings, setPlatformRatings] = useState([false, false, false]);
  const [showContacts, setShowContacts] = useState([false, false, false, false]);
  const [showReviews, setShowReviews] = useState([false, false, false]);

  const contacts = [
    { name: 'Sarah M.', initials: 'SM', color: '#3B82F6' },
    { name: 'John D.', initials: 'JD', color: '#10B981' },
    { name: 'Emma W.', initials: 'EW', color: '#8B5CF6' },
    { name: 'Mike R.', initials: 'MR', color: '#F59E0B' },
  ];

  const reviews = [
    { name: 'Lisa K.', text: '"Excellent service!"', stars: 5, platform: 'google' },
    { name: 'Tom B.', text: '"Highly recommend"', stars: 5, platform: 'feefo' },
    { name: 'Anna S.', text: '"Great experience"', stars: 4, platform: 'meta' },
  ];

  const platforms = [
    { name: 'Google', icon: 'G', color: '#4285F4', rating: 4.9, count: 128 },
    { name: 'Meta', icon: 'M', color: '#0668E1', rating: 4.7, count: 56 },
    { name: 'Feefo', icon: 'F', color: '#FFD700', rating: 4.8, count: 34 },
  ];

  useEffect(() => {
    // Start button pulse
    const pulseTimer = setTimeout(() => {
      setButtonPulse(true);
    }, animationDelay + 300);

    // Animate gauge with elastic bounce
    const gaugeTimer = setTimeout(() => {
      setGaugeValue(75);
    }, animationDelay + 600);

    // Animate platform ratings sequentially
    const platformTimer = setTimeout(() => {
      setPlatformRatings([true, false, false]);
      setTimeout(() => setPlatformRatings([true, true, false]), 200);
      setTimeout(() => setPlatformRatings([true, true, true]), 400);
    }, animationDelay + 800);

    // Animate contacts appearing
    const contactTimer = setTimeout(() => {
      setShowContacts([true, false, false, false]);
      setTimeout(() => setShowContacts([true, true, false, false]), 150);
      setTimeout(() => setShowContacts([true, true, true, false]), 300);
      setTimeout(() => setShowContacts([true, true, true, true]), 450);
    }, animationDelay + 900);

    // Animate reviews appearing
    const reviewTimer = setTimeout(() => {
      setShowReviews([true, false, false]);
      setTimeout(() => setShowReviews([true, true, false]), 300);
      setTimeout(() => setShowReviews([true, true, true]), 600);
    }, animationDelay + 1100);

    // Animate review count
    const countTimer = setTimeout(() => {
      const interval = setInterval(() => {
        setReviewCount(prev => {
          if (prev >= 14) {
            clearInterval(interval);
            return 14;
          }
          return prev + 1;
        });
      }, 60);
    }, animationDelay + 700);

    // Show first notification
    const notifTimer = setTimeout(() => {
      setShowNotification(true);
    }, animationDelay + 2000);

    // Show second notification
    const secondNotifTimer = setTimeout(() => {
      setShowSecondNotification(true);
    }, animationDelay + 3000);

    // Confetti when reaching goal
    const confettiTimer = setTimeout(() => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1500);
    }, animationDelay + 2400);

    return () => {
      clearTimeout(gaugeTimer);
      clearTimeout(countTimer);
      clearTimeout(notifTimer);
      clearTimeout(secondNotifTimer);
      clearTimeout(pulseTimer);
      clearTimeout(confettiTimer);
      clearTimeout(platformTimer);
      clearTimeout(contactTimer);
      clearTimeout(reviewTimer);
    };
  }, [animationDelay]);

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden relative"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Floating Stars Background */}
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="absolute pointer-events-none"
          style={{
            left: `${15 + (i * 15)}%`,
            top: `${20 + (i % 3) * 25}%`,
            opacity: 0,
            animation: `floatStar 3s ease-in-out ${animationDelay + 1000 + (i * 200)}ms infinite`,
          }}
        >
          <Sparkles className="w-2 h-2" style={{ color: '#F59E0B40' }} />
        </div>
      ))}

      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `slideDown 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Reputation
          </span>
          <div className="flex gap-1">
            {['Overview', 'Requests', 'Reviews'].map((tab, i) => (
              <span 
                key={tab}
                className="text-[7px] px-1.5 py-0.5 rounded transition-all duration-300"
                style={{ 
                  backgroundColor: i === 0 ? (isDark ? '#3B82F620' : '#3B82F610') : 'transparent',
                  color: i === 0 ? '#3B82F6' : (isDark ? '#888888' : '#666666'),
                  animation: `fadeIn 0.3s ease-out ${animationDelay + 200 + (i * 50)}ms both`
                }}
              >
                {tab}
              </span>
            ))}
          </div>
        </div>
        <div 
          className="flex items-center gap-1 text-[8px] px-2 py-1 rounded cursor-pointer transition-all duration-300"
          style={{ 
            backgroundColor: isDark ? '#10B98120' : '#10B98110',
            color: '#10B981',
            boxShadow: buttonPulse ? '0 0 12px rgba(16, 185, 129, 0.5)' : 'none',
            animation: buttonPulse ? 'buttonGlow 1.5s ease-in-out infinite' : 'none'
          }}
        >
          <Send className="w-2.5 h-2.5" />
          Send Request
        </div>
      </div>

      {/* Stats Grid - 3 columns */}
      <div 
        className="flex-1 p-2 grid grid-cols-3 gap-1.5 overflow-hidden"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {/* Invites Goal Card with Contacts List */}
        <div 
          className="rounded-lg p-2 flex flex-col relative overflow-hidden"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `cardEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${animationDelay + 300}ms both`
          }}
        >
          {/* Shimmer effect */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(245, 158, 11, 0.1) 50%, transparent 100%)',
              animation: `shimmer 2s ease-in-out ${animationDelay + 1200}ms infinite`
            }}
          />
          <span className="text-[7px] font-medium mb-1" style={{ color: isDark ? '#888888' : '#666666' }}>
            Invites Goal
          </span>
          
          {/* Gauge */}
          <div className="flex items-center justify-center mb-1">
            <svg viewBox="0 0 100 50" className="w-full h-auto max-h-[28px]">
              {/* Background arc */}
              <path
                d="M 10 45 A 35 35 0 0 1 90 45"
                fill="none"
                stroke={isDark ? '#333333' : '#E5E5E5'}
                strokeWidth="6"
                strokeLinecap="round"
              />
              {/* Animated arc with elastic effect */}
              <path
                d="M 10 45 A 35 35 0 0 1 90 45"
                fill="none"
                stroke="#F59E0B"
                strokeWidth="6"
                strokeLinecap="round"
                strokeDasharray="110"
                strokeDashoffset={110 - (110 * gaugeValue / 100)}
                style={{ 
                  transition: 'stroke-dashoffset 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.5))'
                }}
              />
              <text x="50" y="42" textAnchor="middle" fontSize="10" fontWeight="600" fill={isDark ? '#FFFFFF' : '#111111'}>
                15/20
              </text>
            </svg>
          </div>
          
          {/* Contacts List */}
          <div className="space-y-1">
            <span className="text-[6px]" style={{ color: isDark ? '#666666' : '#999999' }}>
              Pending Invites:
            </span>
            <div className="space-y-0.5">
              {contacts.map((contact, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 py-0.5 px-1 rounded"
                  style={{
                    backgroundColor: isDark ? '#2A2A2A' : '#F5F5F5',
                    opacity: showContacts[i] ? 1 : 0,
                    transform: showContacts[i] ? 'translateX(0)' : 'translateX(-10px)',
                    transition: 'all 0.3s ease-out'
                  }}
                >
                  <div
                    className="w-3 h-3 rounded-full flex items-center justify-center text-[5px] text-white font-bold"
                    style={{ backgroundColor: contact.color }}
                  >
                    {contact.initials}
                  </div>
                  <span className="text-[6px]" style={{ color: isDark ? '#CCCCCC' : '#444444' }}>
                    {contact.name}
                  </span>
                  <div className="ml-auto">
                    <Send className="w-2 h-2" style={{ color: '#10B981' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reviews Received Card with Review List */}
        <div 
          className="rounded-lg p-2 flex flex-col"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `cardEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${animationDelay + 400}ms both`
          }}
        >
          <span className="text-[7px] font-medium mb-1" style={{ color: isDark ? '#888888' : '#666666' }}>
            Reviews Received
          </span>
          <div className="flex items-baseline gap-1 mb-1.5">
            <span 
              className="text-base font-bold transition-all duration-300" 
              style={{ 
                color: isDark ? '#FFFFFF' : '#111111',
                textShadow: reviewCount >= 14 ? '0 0 8px rgba(16, 185, 129, 0.5)' : 'none'
              }}
            >
              {reviewCount}
            </span>
            <span className="text-[7px] flex items-center gap-0.5" style={{ color: '#10B981' }}>
              <TrendingUp className="w-2 h-2" style={{ animation: 'bounce 0.5s ease-out' }} />
              +16.67%
            </span>
          </div>
          
          {/* Recent Reviews List */}
          <div className="space-y-1 flex-1">
            {reviews.map((review, i) => (
              <div
                key={i}
                className="p-1 rounded border"
                style={{
                  backgroundColor: isDark ? '#2A2A2A' : '#FAFAFA',
                  borderColor: isDark ? '#3A3A3A' : '#EEEEEE',
                  opacity: showReviews[i] ? 1 : 0,
                  transform: showReviews[i] ? 'translateY(0) scale(1)' : 'translateY(-5px) scale(0.95)',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                <div className="flex items-center gap-1 mb-0.5">
                  <div
                    className="w-3 h-3 rounded-full flex items-center justify-center text-[5px] text-white font-bold"
                    style={{ 
                      backgroundColor: review.platform === 'google' ? '#4285F4' : 
                                       review.platform === 'meta' ? '#0668E1' : '#FFD700'
                    }}
                  >
                    {review.platform === 'google' ? 'G' : review.platform === 'meta' ? 'M' : 'F'}
                  </div>
                  <span className="text-[6px] font-medium" style={{ color: isDark ? '#CCCCCC' : '#444444' }}>
                    {review.name}
                  </span>
                  <div className="ml-auto flex">
                    {[...Array(review.stars)].map((_, s) => (
                      <Star key={s} className="w-1.5 h-1.5" style={{ fill: '#F59E0B', color: '#F59E0B' }} />
                    ))}
                  </div>
                </div>
                <p className="text-[5px] italic" style={{ color: isDark ? '#888888' : '#666666' }}>
                  {review.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Ratings Card */}
        <div 
          className="rounded-lg p-2 flex flex-col"
          style={{ 
            backgroundColor: isDark ? '#222222' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
            animation: `cardEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${animationDelay + 500}ms both`
          }}
        >
          <span className="text-[7px] font-medium mb-1" style={{ color: isDark ? '#888888' : '#666666' }}>
            Platform Ratings
          </span>
          
          {/* Platform List */}
          <div className="space-y-1.5 flex-1">
            {platforms.map((platform, i) => (
              <div
                key={i}
                className="p-1.5 rounded border"
                style={{
                  backgroundColor: isDark ? '#2A2A2A' : '#FAFAFA',
                  borderColor: isDark ? '#3A3A3A' : '#EEEEEE',
                  opacity: platformRatings[i] ? 1 : 0,
                  transform: platformRatings[i] ? 'translateX(0) scale(1)' : 'translateX(10px) scale(0.9)',
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center text-[7px] font-bold"
                    style={{ 
                      backgroundColor: platform.color,
                      color: platform.name === 'Feefo' ? '#111' : '#FFF'
                    }}
                  >
                    {platform.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[7px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                        {platform.name}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <Star className="w-2 h-2" style={{ fill: '#F59E0B', color: '#F59E0B' }} />
                        <span className="text-[7px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                          {platform.rating}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[5px]" style={{ color: isDark ? '#666666' : '#999999' }}>
                        {platform.count} reviews
                      </span>
                      {/* Rating bar */}
                      <div 
                        className="w-12 h-1 rounded-full overflow-hidden"
                        style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }}
                      >
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: platformRatings[i] ? `${(platform.rating / 5) * 100}%` : '0%',
                            backgroundColor: platform.color,
                            transition: 'width 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            transitionDelay: `${i * 150}ms`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Sentiment mini */}
          <div 
            className="flex items-center justify-center gap-3 mt-1 pt-1 border-t"
            style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            <div className="flex items-center gap-1">
              <ThumbsUp className="w-2.5 h-2.5" style={{ color: '#10B981' }} />
              <span className="text-[7px] font-bold" style={{ color: '#10B981' }}>92%</span>
            </div>
            <div className="flex items-center gap-1">
              <ThumbsDown className="w-2.5 h-2.5" style={{ color: '#EF4444' }} />
              <span className="text-[7px] font-bold" style={{ color: '#EF4444' }}>8%</span>
            </div>
          </div>
        </div>
      </div>

      {/* First Notification - New Review */}
      <div
        className="absolute right-2 top-14 rounded-lg p-2 flex items-center gap-2 shadow-lg"
        style={{
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          transform: showNotification ? 'translateX(0)' : 'translateX(120%)',
          opacity: showNotification ? 1 : 0,
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          zIndex: 50,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
        }}
      >
        <div className="w-5 h-5 rounded-full bg-[#4285F4] flex items-center justify-center">
          <span className="text-[6px] text-white font-bold">G</span>
        </div>
        <div>
          <div className="text-[7px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            New 5-Star Review!
          </div>
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={star} 
                className="w-2 h-2" 
                style={{ 
                  fill: '#F59E0B', 
                  color: '#F59E0B',
                  animation: showNotification ? `starPop 0.3s ease-out ${star * 50}ms both` : 'none'
                }} 
              />
            ))}
          </div>
        </div>
      </div>

      {/* Second Notification - Request Sent */}
      <div
        className="absolute right-2 top-28 rounded-lg p-2 flex items-center gap-2 shadow-lg"
        style={{
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#10B98140' : '#10B98130'}`,
          transform: showSecondNotification ? 'translateX(0)' : 'translateX(120%)',
          opacity: showSecondNotification ? 1 : 0,
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          zIndex: 49,
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.2)'
        }}
      >
        <div className="w-5 h-5 rounded-full bg-[#10B981] flex items-center justify-center">
          <CheckCircle className="w-3 h-3 text-white" />
        </div>
        <div>
          <div className="text-[7px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Review request sent
          </div>
          <div className="text-[6px]" style={{ color: isDark ? '#888888' : '#666666' }}>
            to 3 customers
          </div>
        </div>
      </div>

      {/* Confetti Effect */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: '40%',
                backgroundColor: ['#F59E0B', '#10B981', '#3B82F6', '#EF4444'][i % 4],
                animation: `confetti 1s ease-out forwards`,
                animationDelay: `${i * 50}ms`
              }}
            />
          ))}
        </div>
      )}

      {/* Keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes cardEnter {
          from { opacity: 0; transform: scale(0.8) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes starFill {
          from { opacity: 0; transform: scale(0); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes starPop {
          0% { transform: scale(0); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        @keyframes buttonGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 0 16px rgba(16, 185, 129, 0.7); }
        }
        @keyframes floatStar {
          0%, 100% { opacity: 0.3; transform: translateY(0) rotate(0deg); }
          50% { opacity: 0.6; transform: translateY(-8px) rotate(180deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes confetti {
          0% { 
            opacity: 1;
            transform: translateY(0) rotate(0deg) scale(1);
          }
          100% { 
            opacity: 0;
            transform: translateY(-40px) rotate(360deg) scale(0);
          }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
      `}</style>
    </div>
  );
};

export default ReputationContent;
